import React, { useState, useCallback, useEffect, useRef } from "react";
import { useField } from "react-final-form";
import Button from "~/components/button";
import { FormFieldProps } from "~/components/forms/FormFieldProps";
import HelperTextLine from "~/HelperTextLine";
import classnames from "classnames";
import "./FileUploadFormField.scss";
import { Body2 } from "@material/react-typography";

export type FileUploadProps = FormFieldProps & {
  validMessage: string;
  handleChange?: (file: File) => void;
  handleRemove?: () => void;
  onError?: (reportError: (message: string) => void) => void;
};

const FileUploadFormField: React.FC<FileUploadProps> = (
  props: FileUploadProps
) => {
  const {
    formField,
    label,
    helperText,
    required = false,
    disabled = false,
    validMessage,
    handleChange,
    handleRemove,
    onError,
  } = props;

  const checkRequired = (value: any) => {
    if (value && value.name) {
      const fileType: string = value.name.split(".").pop();
      if (!["jpg", "jpeg", "png", "pdf"].includes(fileType.toLowerCase())) {
        return "Invalid file type. Accepted types: .jpg, .jpeg, .png, .pdf";
      }
    }
    if (value || !required || disabled) {
      return;
    }
    return validMessage;
  };

  const {
    meta: { error, submitError, touched },
    input,
  } = useField(formField, { validate: checkRequired });

  const [uploadError, setUploadError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const reportError = useCallback(
    (message: string) => {
      input.onChange(null);
      setUploadError(message);
    },
    [input]
  );

  useEffect(() => {
    if (onError) {
      onError(reportError);
    }
  }, [onError, reportError]);

  const errorMessage = uploadError || error || submitError;
  const isValid = (!touched || !errorMessage) && !submitError && !uploadError;

  const handleFile = (file: File) => {
    setUploadError(null);
    input.onChange(file);
    if (handleChange) {
      handleChange(file);
    }
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (selected) {
      handleFile(selected);
      // Allow re-selecting the same file: without this, picking an identical
      // path twice in a row fires no change event.
      e.target.value = "";
    }
  };

  // A drag can carry several representations of one item -- a uri-list or
  // plain text alongside the file -- and the file is not necessarily first.
  // Prefer `files`, which only ever holds real File objects, then fall back to
  // scanning `items` for the first entry that yields one.
  const extractDroppedFile = (transfer: DataTransfer | null): File | null => {
    if (!transfer) {
      return null;
    }

    const fromFiles = transfer.files[0];
    if (fromFiles) {
      return fromFiles;
    }

    for (const item of Array.from(transfer.items)) {
      if (item.kind !== "file") {
        continue;
      }
      const file = item.getAsFile();
      if (file) {
        return file;
      }
    }

    return null;
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();

    const dropped = extractDroppedFile(e.dataTransfer);

    if (!dropped) {
      setUploadError(
        "That item could not be read as a file. Use the upload button instead."
      );
      return;
    }

    handleFile(dropped);
  };

  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const uploadClass = classnames("file-upload", {
    invalid: !isValid,
  });

  const onDelete = () => {
    input.onChange(null);
    if (handleRemove) {
      handleRemove();
    }
  };

  const browseButtonLabel = `Choose ${label} file`;

  return (
    <div className={uploadClass}>
      {!(input.value && isValid) && (
        <>
          <div
            onDrop={onDrop}
            onDragOver={onDragOver}
            className={`${label}-dropzone file-dropzone`}
          >
            <Body2>
              Drag {label} file here, or click &quot;{browseButtonLabel}&quot;
              below
            </Body2>
          </div>
          <Button type="button" onClick={() => inputRef.current?.click()}>
            {browseButtonLabel}
          </Button>
        </>
      )}

      {input.value && isValid && (
        <div className="filename-container">
          <Body2 className="filename">{input.value.name}</Body2>
          <Button type="button" icon="delete" onClick={onDelete} />
        </div>
      )}
      <input
        ref={inputRef}
        name={formField}
        type="file"
        accept="image/*,application/pdf"
        hidden
        onChange={onChange}
      />
      <HelperTextLine
        message={!isValid && errorMessage ? errorMessage : helperText}
        isValid={isValid}
        persistent={true}
      />
    </div>
  );
};

export default FileUploadFormField;
